import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
// @mui
import {
    Link,
    Stack,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    Alert,
    Divider
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import Iconify from 'components/iconify';
import i18n from 'i18';
import LanguagePopover from 'popovers/LanguagePopover';
import { formatError, hasError } from 'utils/formatError';
import helper from 'utils/helper';
import { useStateSelector } from '../../context/StateSelectors';

// ----------------------------------------------------------------------

export default function ForgotForm() {
    const navigate = useNavigate();
    const server = useStateSelector((state) => state.server);

    const [showPassword, setShowPassword] = useState(false);
    const [username, setUsername] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

    const [buttonText, setButtonText] = useState(i18n.t('auth.forgotContinue'));

    const [step, setStep] = useState(1); // 1,2,3
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleClick = async () => {
        setLoading(true);

        server.auth
            .forgot({
                email: username,
                code,
                twoFactorCode,
                password
            })
            .then((result) => {
                if (!hasError(result)) {
                    let currentStep = step + 1;
                    setLoading(false);
                    setStep(currentStep);
                    if (result.twoFactorEnabled) {
                        setTwoFactorEnabled(true);
                    }
                    if (currentStep === 3) {
                        setLoading(false);
                        setButtonText(i18n.t('auth.forgotSetNewPassword'));
                    } else if (currentStep > 3 && !!result.session) {
                        navigate('/app', { replace: true });
                    }
                } else {
                    setLoading(false);
                    setError(formatError(result));
                }
            })
            .catch((err) => {
                setError(err);
            });
    };

    return (
        <>
            <Helmet>
                <title>{helper.formPageTitle('auth.forgot')}</title>
            </Helmet>

            <Stack direction="row" justifyContent="space-between">
                <Typography variant="h4" gutterBottom>
                    {i18n.t('auth.forgotTitle')}
                </Typography>

                <LanguagePopover forceSmall={true} />
            </Stack>

            <Stack spacing={3} sx={{ mt: 2 }}>
                <TextField
                    name="username"
                    label={i18n.t('columns.email')}
                    value={username}
                    disabled={step > 1}
                    onChange={(event) => setUsername(event.target.value)}
                />

                {step > 1 && (
                    <>
                        <TextField
                            name="code"
                            label={i18n.t('columns.code')}
                            value={code}
                            disabled={step > 2}
                            onChange={(event) => setCode(event.target.value)}
                        />
                    </>
                )}

                {step >= 3 && (
                    <>
                        <TextField
                            name="password"
                            label={i18n.t('columns.newPassword')}
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(event) =>
                                setPassword(event.target.value)
                            }
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() =>
                                                setShowPassword(!showPassword)
                                            }
                                            edge="end"
                                        >
                                            <Iconify
                                                icon={
                                                    showPassword
                                                        ? 'eva:eye-fill'
                                                        : 'eva:eye-off-fill'
                                                }
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        {twoFactorEnabled && (
                            <TextField
                                name="mfaCode"
                                label={i18n.t('columns.mfaCode')}
                                value={twoFactorCode}
                                onChange={(event) =>
                                    setTwoFactorCode(event.target.value)
                                }
                            />
                        )}
                    </>
                )}
            </Stack>

            <LoadingButton
                loading={loading}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                sx={{ my: 2 }}
                onClick={handleClick}
            >
                {buttonText}
            </LoadingButton>

            {error && <Alert severity="error">{error}</Alert>}

            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ my: 2, cursor: 'pointer' }}
            >
                <Link
                    variant="subtitle2"
                    underline="hover"
                    onClick={() => navigate('/auth/login')}
                >
                    {i18n.t('auth.loginLink')}
                </Link>
            </Stack>
        </>
    );
}
